import _ from "lodash";
import React, { useMemo } from "react";
import { useDispatch } from "react-redux";
import queryString from "query-string";
import { fetchGetCrmStatuses } from "../../queries/crmStatuses";
import JsonApi from "../../utils/json_api";
import { DateRangePicker } from "./DateRangeFilter";
import moment from "moment";

export const useCrmFilters = (
  queryCache: any,
  employeesQuery: any,
  topicsQuery: any,
  departmentsQuery: any,
  statusesQuery: any,
  aggregations: any,
) => {
  const dispatch = useDispatch();
  const statusesData = useMemo(() => _.get(statusesQuery, "data.data", []), [statusesQuery.data]);
  const options = useMemo(
    () => ({
      filters: {
        id: {
          hidden: true,
        },
        q: {
          label: "Matches",
          max: 1,
          collapse: (values: string[]) => values.join(" "),
        },
        displayId: {
          label: "Request ID",
          keywords: ["display", "case", "request", "ticket"],
          max: 1,
          render: "freeform",
          transient: true,
          resolver: (context: any, value: any) => {
            return [
              value,
              {
                q: (context["q"] || []).concat(`(displayId.search:${value})`),
              },
            ];
          },
        },
        requester: {
          label: "Requester",
          keywords: ["from"],
          max: 1,
          render: "freeform",
          transient: true,
          resolver: (context: any, value: any) => {
            const query = `((contact.firstName:${value}) OR (contact.lastName:${value}))`;
            return [
              value,
              {
                q: (context["q"] || []).concat(`${query}`),
              },
            ];
          },
        },
        linkedCodeCase: {
          label: "Linked Code Case",
          transient: true,
          render: "boolean",
          resolver: (context: any, value: boolean) => {
            return [
              value,
              {
                "has.cecaseId": value,
              },
            ];
          },
        },
        dateEntered: {
          label: "Date Entered",
          max: 1,
          array: false,
          options: [
            {
              key: "range",
              value: "",
              text: "Pick Range",
            },
          ],
          resolver: (context: any, value: any) => {
            return _.isEmpty(value) ? [undefined] : [value];
          },
          render: ({ onChange, values }: any) => {
            return (
              <>
                Date Entered:
                <DateRangePicker onChange={onChange} value={values[0]} />
              </>
            );
          },
        },
        dueThis: {
          label: "Due This",
          max: 1,
          transient: true,
          exclusiveTo: ["pastDue", "dueToday", "dueWithin"],
          options: [
            {
              key: "week",
              value: "week",
              text: "week",
            },
            {
              key: "month",
              value: "month",
              text: "month",
            },
            {
              key: "year",
              value: "year",
              text: "year",
            },
          ],
          resolver: async (context: any, value: any) => {
            return [
              value,
              {
                dateExpectClose: {
                  start: moment().format("YYYY-MM-DD"),
                  end: moment().endOf(value).format("YYYY-MM-DD"),
                },
              },
            ];
          },
        },
        dueToday: {
          label: "Due Today",
          transient: true,
          render: "boolean",
          exclusiveTo: ["pastDue", "dueThis", "dueWithin"],
          resolver: (context: any, value: boolean) => {
            if (value) {
              return [
                value,
                {
                  dateExpectClose: {
                    start: moment().startOf("day").format("YYYY-MM-DD"),
                    end: moment().startOf("day").format("YYYY-MM-DD"),
                  },
                },
              ];
            } else {
              return [
                value,
                {
                  dateExpectClose: {
                    start: moment().add(1, "day").startOf("day").format("YYYY-MM-DD"),
                  },
                },
              ];
            }
          },
        },
        dueWithin: {
          label: "Due Within",
          max: 1,
          transient: true,
          exclusiveTo: ["dueToday", "pastDue", "dueThis"],
          options: [
            {
              key: "1 weeks",
              value: "1 weeks",
              text: "1 week",
            },
            {
              key: "2 weeks",
              value: "2 weeks",
              text: "2 weeks",
            },
            {
              key: "1 months",
              value: "1 months",
              text: "1 month",
            },
            {
              key: "6 months",
              value: "6 months",
              text: "6 months",
            },
          ],
          resolver: async (context: any, value: any) => {
            const [amount, units] = value.split(" ");
            return [
              value,
              {
                dateExpectClose: {
                  start: moment().format("YYYY-MM-DD"),
                  end: moment().add(amount, units).startOf("day").format("YYYY-MM-DD"),
                },
              },
            ];
          },
        },
        pastDue: {
          label: "Past Due",
          transient: true,
          render: "boolean",
          exclusiveTo: ["dueThis", "dueToday", "dueWithin"],
          resolver: (context: any, value: boolean) => {
            if (value) {
              return [
                value,
                {
                  dateExpectClose: {
                    end: moment().startOf("day").format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
                  },
                },
              ];
            } else {
              return [
                value,
                {
                  dateExpectClose: {
                    start: moment().startOf("day").format("YYYY-MM-DDTHH:mm:ss.SSSZ"),
                  },
                },
              ];
            }
          },
        },
        status: {
          label: "Status",
          options: statusesData.map((data: any) => ({
            key: data.id || data.attributes.name,
            value: data.id || data.attributes.name,
            text: data.attributes.name,
          })),
          array: true,
          exclusiveTo: ["statusCategory"],
          resolver: async (context: any, value: any) => {
            const status = JsonApi.findById(statusesData, value);
            const resolveAs = _.get(status, "attributes.name") || value;
            return [resolveAs, { statusCategory: undefined }];
          },
        },
        statusCategory: {
          label: "Status Category",
          max: 1,
          array: true,
          exclusiveTo: ["status"],
          transient: true,
          options: [
            {
              key: "open",
              value: "open",
              text: "Open",
            },
            {
              key: "closed",
              value: "closed",
              text: "Closed",
            },
          ],
          resolver: async (context: any, value: any) => {
            const statusesQuery = await queryCache.prefetchQuery(["crmStatuses"], fetchGetCrmStatuses(dispatch), {
              staleTime: Infinity,
            });
            const statuses = _.get(statusesQuery, "payload.data", [])
              .filter((status: any) => status.attributes.category === value)
              .map((status: any) => status.attributes.name);
            return [
              undefined,
              {
                status: statuses,
              },
            ];
          },
        },
        topicId: {
          label: "Topic",
          array: true,
          query: topicsQuery,
          transient: true,
          toText: (data: any) => data.attributes.name,
          resolver: (context: any, value: any) => {
            return [
              value,
              {
                classificationId: [...(context.topicId || []), value],
              },
            ];
          },
        },
        departmentId: {
          label: "Department",
          array: true,
          query: departmentsQuery,
          toText: (data: any) => data.attributes.name,
        },
        assignedToId: {
          label: "Assigned To",
          array: true,
          options: [
            {
              key: "me",
              value: "me",
              text: "Me",
            },
          ],
          query: employeesQuery,
          toText: (data: any) => `${data.attributes["first-name"]} ${data.attributes["last-name"]}`,
        },
        priority: {
          label: "Priority",
          options: _.get(aggregations, "options.priority", []),
          array: true,
        },
      },
    }),
    [employeesQuery, topicsQuery, departmentsQuery, statusesData, aggregations.options],
  );
  const initialFilterState = useMemo(() => {
    const query = queryString.parse(location.search, { arrayFormat: "bracket", parseBooleans: true });
    const tempQuery: any = {
      ...query,
      has_balance: query?.has_balance !== undefined ? String(query?.has_balance) : undefined,
    };
    if (query?.has_balance === undefined) {
      delete tempQuery.has_balance;
    }
    const newState = _.reduce(
      tempQuery,
      (result: any, values: any, key: string) => {
        if (
          [
            "statusCategory",
            "displayId",
            "q",
            "requester",
            "linkedCodeCase",
            "dateEntered",
            "dueThis",
            "dueToday",
            "dueWithin",
            "pastDue",
            "status",
            "topicId",
            "departmentId",
            "assignedToId",
            "priority",
            "clearfilters",
          ].includes(key)
        ) {
          result[key] = tempQuery[key];
        }
        return result;
      },
      {},
    );

    // Set default filters
    // TODO: This should probably be driven by the query params alone
    if (_.isEmpty(newState)) {
      newState.assignedToId = ["me"];
      newState.statusCategory = ["open"];
    }

    return newState;
  }, []);

  return {
    ...options,
    initialFilterState: initialFilterState,
  };
};
export default useCrmFilters;
